import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, MARYLINE_AVATAR } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { ArrowLeft, Send, ShieldCheck, Sparkles, Heart, AlertTriangle, RotateCcw } from "lucide-react";

const CID_KEY = "rawlove_conversation_id";
const PENDING_KEY = "rawlove_pending_cid";
const newLocalId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function Bubble({ role, content, status, onRetry }) {
  const isUser = role === "user";
  const failed = isUser && status === "failed";
  return (
    <div className={`flex flex-col ${isUser ? "items-end" : "items-start"} animate-bubble`}>
      <div className={`flex w-full ${isUser ? "justify-end" : "justify-start"}`}>
        {!isUser && (
          <img src={MARYLINE_AVATAR} alt="Maryline"
            className="w-8 h-8 rounded-full object-cover mr-2 mt-1 flex-shrink-0 border border-[#E8DDD5]" />
        )}
        <div
          className={`max-w-[80%] px-4 py-3 text-[15px] leading-relaxed whitespace-pre-wrap transition-opacity ${
            isUser
              ? "bg-[#6E2D38] text-white rounded-2xl rounded-tr-sm"
              : "bg-[#F2E9E1] text-[#2B1E1B] rounded-2xl rounded-tl-sm"
          } ${failed ? "opacity-60" : ""}`}
          data-testid={isUser ? "user-bubble" : "maryline-bubble"}
          data-status={isUser ? status || "processed" : undefined}
        >
          {content}
        </div>
      </div>
      {failed && (
        <div className="flex items-center gap-2 mt-1.5 mr-1 text-xs text-[#A83A3A]" data-testid="message-failed-notice">
          <AlertTriangle className="w-3.5 h-3.5" />
          <span>Non traité</span>
          <button onClick={onRetry} data-testid="retry-message-button"
            className="inline-flex items-center gap-1 font-medium underline underline-offset-2 hover:text-[#6E2D38] transition-colors">
            <RotateCcw className="w-3.5 h-3.5" /> Réessayer
          </button>
        </div>
      )}
    </div>
  );
}

export default function Chat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [sending, setSending] = useState(false);
  const [depth, setDepth] = useState(0);
  const [ready, setReady] = useState(false);
  const [showOffer, setShowOffer] = useState(false);
  const [safety, setSafety] = useState(false);
  const [cid, setCid] = useState(null);
  const [booting, setBooting] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [email, setEmail] = useState("");

  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  };

  // Identity of the journey lives in the URL (?c=) — never silently resumed from a previous analysis.
  // ?new=1  → always start a fresh conversation (Home CTAs, "Nouvelle analyse").
  // ?c=ID   → resume / reopen exactly that conversation (refresh, e-mail link, "Reprendre").
  // no param → resume the stored unpaid conversation if any, otherwise start fresh.
  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const urlCid = params.get("c");
      const forceNew = params.has("new");
      const stored = forceNew ? null : (urlCid || localStorage.getItem(CID_KEY));

      const startFresh = async () => {
        localStorage.removeItem(CID_KEY);
        localStorage.removeItem(PENDING_KEY);
        const { data } = await api.post("/conversation/start");
        localStorage.setItem(CID_KEY, data.conversation_id);
        setCid(data.conversation_id);
        setMessages([{ role: "assistant", content: data.message, localId: newLocalId() }]);
        setDepth(0); setReady(false); setSafety(false);
        navigate(`/chat?c=${data.conversation_id}`, { replace: true });
      };

      try {
        if (stored) {
          const { data } = await api.get(`/conversation/${stored}`);
          if (data.paid) {
            if (urlCid) { navigate(`/result/${stored}`, { replace: true }); return; }
            await startFresh(); // a finished, paid analysis is never reused as a new journey
            return;
          }
          setCid(stored);
          localStorage.setItem(CID_KEY, stored);
          setMessages(data.messages.map((m) => ({
            role: m.role, content: m.content, localId: newLocalId(),
            messageId: m.message_id, status: m.status,
          })));
          setDepth(data.depth || 0);
          setReady(data.ready_for_analysis || false);
          setSafety(data.safety_flag || false);
          if (!urlCid) navigate(`/chat?c=${stored}`, { replace: true });
        } else {
          await startFresh();
        }
      } catch (e) {
        await startFresh();
      } finally {
        setBooting(false);
        scrollToBottom();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(scrollToBottom, [messages, typing, showOffer]);

  useEffect(() => {
    if (!ready) { setShowOffer(false); return undefined; }
    const t = setTimeout(() => setShowOffer(true), booting ? 0 : 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const autoGrow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  };

  const applyTurn = (data) => {
    setDepth(data.depth || 0);
    setReady(data.ready_for_analysis || false);
    setSafety(data.safety_flag || false);
  };

  const markMessage = (localId, patch) =>
    setMessages((m) => m.map((x) => (x.localId === localId ? { ...x, ...patch } : x)));

  const runTurn = async (localId, request) => {
    setSending(true);
    setTyping(true);
    try {
      const { data } = await request();
      setTyping(false);
      markMessage(localId, { status: "processed", messageId: data.message_id });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, localId: newLocalId() }]);
      applyTurn(data);
    } catch (e) {
      setTyping(false);
      const body = e?.response?.data || {};
      if (e?.response?.status === 409) {
        toast.message("Maryline est encore en train de répondre à ce message.");
        return;
      }
      markMessage(localId, { status: "failed", messageId: body.message_id || undefined });
      toast.error(body.detail || "Un souci est survenu. Ton message est conservé, tu peux réessayer.");
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    const content = input.trim();
    if (!content || sending || !cid) return;
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";
    const localId = newLocalId();
    const clientMessageId = newLocalId();
    setMessages((m) => [...m, { role: "user", content, localId, clientMessageId, status: "pending" }]);
    await runTurn(localId, () =>
      api.post("/conversation/message", { conversation_id: cid, content, client_message_id: clientMessageId }));
  };

  const retry = async (msg) => {
    if (sending || !cid) return;
    markMessage(msg.localId, { status: "pending" });
    await runTurn(msg.localId, () =>
      msg.messageId
        ? api.post("/conversation/message/retry", { conversation_id: cid, message_id: msg.messageId })
        : api.post("/conversation/message", {
            conversation_id: cid, content: msg.content, client_message_id: msg.clientMessageId }));
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const goCheckout = async () => {
    if (checkoutLoading || !cid) return;
    if (!emailValid) {
      toast.error("Une adresse e-mail valide est nécessaire pour recevoir l'analyse.");
      return;
    }
    setCheckoutLoading(true);
    trackEvent("checkout_viewed", cid);
    try {
      const { data } = await api.post("/payments/checkout", {
        conversation_id: cid,
        origin_url: window.location.origin,
        email: email.trim(),
      });
      localStorage.setItem(PENDING_KEY, cid);
      window.location.href = data.checkout_url;
    } catch (e) {
      setCheckoutLoading(false);
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le paiement pour le moment.");
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#FAF7F2]">
      {/* Header */}
      <header className="flex-shrink-0 backdrop-blur-xl bg-[#FFFDF9]/90 border-b border-[#E8DDD5]">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/")} data-testid="chat-back-button"
            className="p-2 -ml-2 rounded-full hover:bg-[#F2E9E1] transition-colors">
            <ArrowLeft className="w-5 h-5 text-[#5C4A45]" />
          </button>
          <img src={MARYLINE_AVATAR} alt="Maryline"
            className="w-10 h-10 rounded-full object-cover border border-[#E8DDD5]" />
          <div className="flex-1">
            <p className="font-semibold text-[15px] leading-tight flex items-center gap-2">
              Maryline
              <span className="w-2 h-2 rounded-full bg-[#2E6B4F]" />
            </p>
            <p className="text-xs text-[#8C6B64] leading-tight">Votre confidente</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[#8C6B64]">
            <ShieldCheck className="w-4 h-4 text-[#6E2D38]" />
            <span className="hidden sm:inline">Confidentiel</span>
          </div>
        </div>
        {depth > 0 && !ready && (
          <div className="max-w-2xl mx-auto px-4 pb-2">
            <div className="flex items-center justify-between text-[11px] text-[#8C6B64] mb-1">
              <span>Compréhension de votre histoire</span>
              <span>{Math.min(depth, 99)}%</span>
            </div>
            <div className="h-1 rounded-full bg-[#F2E9E1] overflow-hidden">
              <div className="h-full bg-[#C48B92] rounded-full transition-all duration-700"
                style={{ width: `${Math.min(depth, 99)}%` }} />
            </div>
          </div>
        )}
      </header>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
          {booting && (
            <div className="flex items-center gap-2 text-[#8C6B64] text-sm">
              <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
            </div>
          )}
          {messages.map((m) => (
            <Bubble key={m.localId} role={m.role} content={m.content} status={m.status}
              onRetry={() => retry(m)} />
          ))}

          {typing && (
            <div className="flex justify-start animate-bubble" data-testid="typing-indicator">
              <img src={MARYLINE_AVATAR} alt="Maryline"
                className="w-8 h-8 rounded-full object-cover mr-2 mt-1 border border-[#E8DDD5]" />
              <div className="bg-[#F2E9E1] rounded-2xl rounded-tl-sm px-4 py-3.5 flex items-center gap-1.5">
                <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
              </div>
            </div>
          )}

          {safety && (
            <div className="bg-[#FDF0F0] border border-[#A83A3A]/25 rounded-2xl p-4 flex gap-3 animate-fade-up"
              data-testid="safety-notice">
              <AlertTriangle className="w-5 h-5 text-[#A83A3A] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#7a2b2b] leading-relaxed">
                Ta sécurité passe avant tout. Si tu es en danger immédiat, contacte le 17 (police) ou le 3919
                (violences conjugales, France). RawLove n'est pas un service d'urgence.
              </p>
            </div>
          )}

          {ready && showOffer && (
            <div className="bg-white border border-[#C48B92]/40 rounded-2xl p-6 shadow-[0_12px_30px_-8px_rgba(110,45,56,0.15)] animate-fade-up"
              data-testid="analysis-ready-panel">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#6E2D38]" />
                <h3 className="font-editorial text-xl font-semibold">Pour aller plus loin, si l'envie est là</h3>
              </div>
              <p className="text-[15px] text-[#5C4A45] leading-relaxed mb-5" data-testid="analysis-offer-intro">
                Maryline a rassemblé tout ce qui a été confié ici. L'analyse complète RawLove reprend ces éléments
                un par un, avec le recul qu'ils méritent — sans jamais prétendre lire dans les pensées de l'autre.
              </p>
              <div className="bg-[#FAF7F2] rounded-xl p-4 border border-[#E8DDD5] mb-5">
                <p className="font-medium mb-2">L'analyse complète comprend :</p>
                <ul className="text-sm text-[#5C4A45] space-y-1.5">
                  {["Ce que ses comportements montrent", "Red & Green Flags",
                    "Dynamique relationnelle & compatibilité", "Faits vs interprétations",
                    "La réponse à ta question", "Ton plan d'action sur-mesure"].map((t) => (
                    <li key={t} className="flex items-center gap-2">
                      <Heart className="w-3 h-3 text-[#6E2D38] fill-[#6E2D38]" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mb-3">
                <label className="block text-sm font-medium text-[#4A3B37] mb-1.5">
                  Adresse e-mail pour recevoir l'analyse
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="prenom@exemple.com"
                  data-testid="checkout-email-input"
                  className="w-full rounded-xl border border-[#E8DDD5] px-4 py-3 outline-none focus:border-[#C48B92] transition-colors text-[15px]"
                />
              </div>
              <button onClick={goCheckout} disabled={checkoutLoading}
                data-testid="stripe-checkout-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#6E2D38] hover:bg-[#54212A] disabled:opacity-60 text-white rounded-full px-6 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-md">
                {checkoutLoading ? "Redirection…" : "Lire l'analyse complète — 4,99 €"}
              </button>
              <p className="text-center text-xs text-[#8C6B64] mt-3" data-testid="analysis-offer-footnote">
                Rien ne presse : cette conversation reste accessible. Paiement sécurisé par Stripe · analyse aussi envoyée par e-mail.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="flex-shrink-0 border-t border-[#E8DDD5] bg-[#FFFDF9]">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-end gap-2 bg-white rounded-3xl border border-[#E8DDD5] px-4 py-2 focus-within:border-[#C48B92] transition-colors">
            <textarea
              ref={taRef}
              rows={1}
              value={input}
              onChange={(e) => { setInput(e.target.value); autoGrow(); }}
              onKeyDown={onKeyDown}
              placeholder="Racontez à Maryline ce qui vous préoccupe…"
              data-testid="chat-message-input"
              className="flex-1 resize-none bg-transparent outline-none text-[15px] py-2 max-h-[140px] placeholder:text-[#B9A79E]"
            />
            <button onClick={send} disabled={!input.trim() || sending}
              data-testid="chat-send-button"
              className="flex-shrink-0 w-10 h-10 rounded-full bg-[#6E2D38] hover:bg-[#54212A] disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition-all">
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="text-center text-[11px] text-[#B9A79E] mt-2">
            Vos échanges restent confidentiels. <a href="/a-propos" data-testid="chat-about-link" className="underline underline-offset-2 hover:text-[#6E2D38]">En savoir plus</a>
          </p>
        </div>
      </div>
    </div>
  );
}
